import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayClassName } from "@/components/ui/HubModalOverlay";
import { FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX, isFloorTabletDevice } from "@/lib/floorTabletDevice";
import { HUB_MODAL_OVERLAY_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

import {
  buildCameraVideoConstraints,
  cameraPreviewBounds,
  computeObjectCoverSourceRect,
  fallbackCameraPreviewAspect,
  fitCameraPreviewBox,
  isCompactCameraCaptureViewport,
  normalizeCameraPreviewAspect,
  readCameraCaptureDeviceKind,
  readCameraCaptureViewport,
  readCameraCaptureViewportForModal,
  readHubMainContentWidth,
  type CameraCaptureViewport,
} from "./cameraCaptureLayout";
import { resolveCameraCaptureModalLayout } from "./resolveCameraCaptureModalLayout";
import { readCameraCaptureModalLayoutFromWindow } from "./readCameraCaptureModalLayoutFromWindow";

function stopMediaStream(stream: MediaStream | null): void {
  if (stream == null) return;
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

function buildPhotoFilename(prefix: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `${prefix}-${stamp}.jpg`;
}

export function CameraCaptureModal({
  open,
  title = "Take photo",
  hint,
  filenamePrefix = "packing-slip",
  jpegQuality = 0.92,
  maxLongEdge,
  onClose,
  onCapture,
}: {
  open: boolean;
  title?: string;
  hint?: string;
  filenamePrefix?: string;
  jpegQuality?: number;
  maxLongEdge?: number;
  onClose: () => void;
  onCapture: (file: File) => void;
}): JSX.Element | null {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const viewportRef = useRef<CameraCaptureViewport>(readCameraCaptureViewportForModal());
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [viewport, setViewport] = useState<CameraCaptureViewport>(() => readCameraCaptureViewportForModal());
  const [pageContentWidth, setPageContentWidth] = useState(() => readHubMainContentWidth());
  const [previewAspect, setPreviewAspect] = useState(() =>
    fallbackCameraPreviewAspect(readCameraCaptureViewport().orientation),
  );
  const [, setLayoutTick] = useState(0);

  const liveLayoutInputs = open ? readCameraCaptureModalLayoutFromWindow() : null;
  const layoutViewport = liveLayoutInputs?.viewport ?? viewport;
  const layoutPageContentWidth = liveLayoutInputs?.pageContentWidth ?? pageContentWidth;

  const touchCoarse =
    typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const touchHoverNone =
    typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;
  const floorTablet = isFloorTabletDevice({
    coarsePointer: touchCoarse,
    hoverNone: touchHoverNone,
    viewportWidth: layoutViewport.width,
  });
  const modalLayout = resolveCameraCaptureModalLayout({
    viewport: layoutViewport,
    pageContentWidth: layoutPageContentWidth,
    previewAspect,
    touchCoarse,
    touchHoverNone,
    floorTablet,
    hasHint: hint != null && hint.trim() !== "",
  });
  const fillViewportPreview = modalLayout.fillViewportPreview;
  const filledLayout = modalLayout.filledLayout;
  const compactCaptureLayout = isCompactCameraCaptureViewport(layoutViewport.width);
  const previewBounds = cameraPreviewBounds({
    viewportWidth: layoutViewport.width,
    viewportHeight: layoutViewport.height,
    floorTablet,
    wideTouchKiosk:
      (touchCoarse || touchHoverNone) && layoutViewport.width > FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX,
    compactViewport: compactCaptureLayout,
    orientation: layoutViewport.orientation,
  });
  const previewBox = fitCameraPreviewBox({
    aspectRatio: previewAspect,
    maxWidth: previewBounds.maxWidth,
    maxHeight: previewBounds.maxHeight,
  });

  useEffect(() => {
    if (!open) return;

    const syncViewport = (): void => {
      const next = readCameraCaptureViewportForModal();
      const nextPageContentWidth = readHubMainContentWidth();
      const prev = viewportRef.current;
      const viewportUnchanged =
        prev.width === next.width
        && prev.height === next.height
        && prev.orientation === next.orientation;
      viewportRef.current = next;
      setPageContentWidth(nextPageContentWidth);
      if (viewportUnchanged) {
        return;
      }
      setViewport(next);
      setPreviewAspect(() => {
        const video = videoRef.current;
        const previewOrientation =
          readCameraCaptureDeviceKind({
            floorTablet: isFloorTabletDevice({
              coarsePointer: window.matchMedia("(pointer: coarse)").matches,
              hoverNone: window.matchMedia("(hover: none)").matches,
              viewportWidth: next.width,
            }),
            wideTouchKiosk:
              (window.matchMedia("(pointer: coarse)").matches
                || window.matchMedia("(hover: none)").matches)
              && next.width > FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX,
            touchCoarse: window.matchMedia("(pointer: coarse)").matches,
            touchHoverNone: window.matchMedia("(hover: none)").matches,
          }) === "desktop_webcam"
            ? "portrait"
            : next.orientation;
        if (video != null && video.videoWidth > 0 && video.videoHeight > 0) {
          return normalizeCameraPreviewAspect({
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            orientation: previewOrientation,
          });
        }
        return fallbackCameraPreviewAspect(previewOrientation);
      });
    };

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleSyncViewport = (): void => {
      if (resizeTimer != null) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        syncViewport();
      }, 120);
    };

    syncViewport();
    window.addEventListener("resize", scheduleSyncViewport);
    window.addEventListener("orientationchange", syncViewport);
    window.visualViewport?.addEventListener("resize", scheduleSyncViewport);
    return () => {
      if (resizeTimer != null) clearTimeout(resizeTimer);
      window.removeEventListener("resize", scheduleSyncViewport);
      window.removeEventListener("orientationchange", syncViewport);
      window.visualViewport?.removeEventListener("resize", scheduleSyncViewport);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    let frame1 = 0;
    let frame2 = 0;
    const syncLayout = (): void => {
      const next = readCameraCaptureModalLayoutFromWindow();
      viewportRef.current = next.viewport;
      setViewport(next.viewport);
      setPageContentWidth(next.pageContentWidth);
      setLayoutTick((tick) => tick + 1);
    };
    syncLayout();
    frame1 = requestAnimationFrame(() => {
      syncLayout();
      frame2 = requestAnimationFrame(syncLayout);
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const initialViewport = readCameraCaptureViewportForModal();
    viewportRef.current = initialViewport;
    setViewport(initialViewport);
    setPageContentWidth(readHubMainContentWidth());
    const initialDeviceKind = readCameraCaptureDeviceKind({
      floorTablet: isFloorTabletDevice({
        coarsePointer: window.matchMedia("(pointer: coarse)").matches,
        hoverNone: window.matchMedia("(hover: none)").matches,
        viewportWidth: initialViewport.width,
      }),
      wideTouchKiosk:
        (window.matchMedia("(pointer: coarse)").matches
          || window.matchMedia("(hover: none)").matches)
        && initialViewport.width > FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX,
      touchCoarse: window.matchMedia("(pointer: coarse)").matches,
      touchHoverNone: window.matchMedia("(hover: none)").matches,
    });
    setPreviewAspect(
      fallbackCameraPreviewAspect(
        initialDeviceKind === "desktop_webcam" ? "portrait" : initialViewport.orientation,
      ),
    );
    setError(null);
    setReady(false);
    setCapturing(false);
    let cancelled = false;
    let removeVideoListeners: (() => void) | null = null;

    async function startCamera(): Promise<void> {
      if (navigator.mediaDevices?.getUserMedia == null) {
        setError("Camera access is not available in this browser.");
        return;
      }

      const orientation = viewportRef.current.orientation;
      const captureDeviceKind = readCameraCaptureDeviceKind({
        floorTablet: isFloorTabletDevice({
          coarsePointer: window.matchMedia("(pointer: coarse)").matches,
          hoverNone: window.matchMedia("(hover: none)").matches,
          viewportWidth: viewportRef.current.width,
        }),
        wideTouchKiosk:
          (window.matchMedia("(pointer: coarse)").matches
            || window.matchMedia("(hover: none)").matches)
          && viewportRef.current.width > FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX,
        touchCoarse: window.matchMedia("(pointer: coarse)").matches,
        touchHoverNone: window.matchMedia("(hover: none)").matches,
      });

      try {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: buildCameraVideoConstraints(orientation, captureDeviceKind),
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video:
              captureDeviceKind === "desktop_webcam"
                ? { facingMode: { ideal: "user" }, frameRate: { ideal: 30, max: 30 } }
                : { facingMode: { ideal: "environment" } },
            audio: false,
          });
        }

        if (cancelled) {
          stopMediaStream(stream);
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (video == null) {
          stopMediaStream(stream);
          streamRef.current = null;
          setError("Could not attach the camera preview.");
          return;
        }

        const syncPreviewAspect = (): void => {
          if (cancelled) return;
          const captureDeviceKind = readCameraCaptureDeviceKind({
            floorTablet: isFloorTabletDevice({
              coarsePointer: window.matchMedia("(pointer: coarse)").matches,
              hoverNone: window.matchMedia("(hover: none)").matches,
              viewportWidth: viewportRef.current.width,
            }),
            wideTouchKiosk:
              (window.matchMedia("(pointer: coarse)").matches
                || window.matchMedia("(hover: none)").matches)
              && viewportRef.current.width > FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX,
            touchCoarse: window.matchMedia("(pointer: coarse)").matches,
            touchHoverNone: window.matchMedia("(hover: none)").matches,
          });
          const previewOrientation =
            captureDeviceKind === "desktop_webcam" ? "portrait" : viewportRef.current.orientation;
          setPreviewAspect(
            normalizeCameraPreviewAspect({
              videoWidth: video.videoWidth,
              videoHeight: video.videoHeight,
              orientation: previewOrientation,
            }),
          );
        };

        video.addEventListener("loadedmetadata", syncPreviewAspect);
        video.addEventListener("resize", syncPreviewAspect);
        removeVideoListeners = () => {
          video.removeEventListener("loadedmetadata", syncPreviewAspect);
          video.removeEventListener("resize", syncPreviewAspect);
        };

        video.srcObject = stream;
        await video.play();
        syncPreviewAspect();
        if (!cancelled) setReady(true);
      } catch (cameraError) {
        if (cancelled) return;
        const message =
          cameraError instanceof DOMException && cameraError.name === "NotAllowedError"
            ? "Camera access was blocked. Allow the camera when prompted, or use Upload file instead."
            : cameraError instanceof Error
              ? cameraError.message
              : "Could not open the camera.";
        setError(message);
      }
    }

    void startCamera();

    return () => {
      cancelled = true;
      removeVideoListeners?.();
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      const video = videoRef.current;
      if (video != null) video.srcObject = null;
    };
  }, [open]);

  if (!open) return null;

  function capturePhoto(): void {
    const video = videoRef.current;
    if (video == null || !ready || capturing) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width <= 0 || height <= 0) return;

    const previewContainer = previewContainerRef.current;
    const containerWidth = previewContainer?.clientWidth ?? modalLayout.previewWidth;
    const containerHeight = previewContainer?.clientHeight ?? modalLayout.previewHeight;
    const crop = computeObjectCoverSourceRect({
      videoWidth: width,
      videoHeight: height,
      containerWidth,
      containerHeight,
    });

    setCapturing(true);
    const cropLongEdge = Math.max(crop.sw, crop.sh);
    const scale =
      maxLongEdge != null && maxLongEdge > 0 && cropLongEdge > maxLongEdge ? maxLongEdge / cropLongEdge : 1;
    const drawWidth = Math.max(1, Math.round(crop.sw * scale));
    const drawHeight = Math.max(1, Math.round(crop.sh * scale));
    const canvas = document.createElement("canvas");
    canvas.width = drawWidth;
    canvas.height = drawHeight;
    const context = canvas.getContext("2d");
    if (context == null) {
      setCapturing(false);
      return;
    }
    context.drawImage(
      video,
      crop.sx,
      crop.sy,
      crop.sw,
      crop.sh,
      0,
      0,
      drawWidth,
      drawHeight,
    );
    canvas.toBlob(
      (blob) => {
        setCapturing(false);
        if (blob == null) {
          setError("Could not capture the photo.");
          return;
        }
        onCapture(new File([blob], buildPhotoFilename(filenamePrefix), { type: "image/jpeg" }));
        onClose();
      },
      "image/jpeg",
      jpegQuality,
    );
  }

  return createPortal(
    <div
      data-hub-modal-overlay=""
      className={cn(
        hubModalOverlayClassName(true),
        fillViewportPreview ? "min-h-[100dvh] items-center justify-center p-2" : "items-start",
      )}
      style={{ zIndex: HUB_MODAL_OVERLAY_Z_INDEX }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="camera-capture-title"
    >
      <Card
        data-camera-capture-probe-card=""
        data-camera-capture-layout-stuck-at-minimum={modalLayout.stuckAtMinimum ? "true" : "false"}
        data-camera-capture-layout-inputs-ready={modalLayout.inputsReady ? "true" : "false"}
        data-camera-capture-layout-page-width={String(layoutPageContentWidth)}
        data-camera-capture-layout-viewport-height={String(layoutViewport.height)}
        className={cn(
          "relative w-full shadow-xl",
          fillViewportPreview ? "max-h-[calc(100dvh-2rem)] overflow-y-auto" : "my-4 max-w-xl",
        )}
        style={
          fillViewportPreview && filledLayout != null
            ? {
                width: "100%",
                maxWidth: `${String(modalLayout.cardWidth)}px`,
                maxHeight: modalLayout.cardMaxHeight,
              }
            : undefined
        }
      >
        <CardHeader className={fillViewportPreview ? "shrink-0" : undefined}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h3 id="camera-capture-title" className="text-base font-semibold text-slate-900">
                {title}
              </h3>
              {hint != null && hint.trim() !== "" ? (
                <p className="mt-1 text-sm text-slate-600">{hint}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close camera"
              disabled={capturing}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-4 space-y-0">
          <div
            ref={previewContainerRef}
            data-camera-capture-probe-preview=""
            data-camera-capture-expected-preview-width={String(modalLayout.previewWidth)}
            data-camera-capture-expected-preview-height={String(modalLayout.previewHeight)}
            className="relative mx-auto shrink-0 grow-0 overflow-hidden rounded-lg border border-slate-200 bg-black"
            style={
              fillViewportPreview
                ? modalLayout.previewStyle
                : {
                    width: `${String(previewBox.width)}px`,
                    height: `${String(previewBox.height)}px`,
                    maxWidth: "100%",
                  }
            }
          >
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover"
              playsInline
              muted
              autoPlay
              disablePictureInPicture
            />
            {!ready && error == null ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-950/70 text-sm text-white">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Opening camera…
              </div>
            ) : null}
          </div>
          {error != null ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={capturing}>
              Cancel
            </Button>
            <Button type="button" onClick={capturePhoto} disabled={!ready || capturing || error != null}>
              {capturing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Camera className="h-4 w-4" aria-hidden />}
              Use photo
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
